(()=>{
  const instances=new WeakMap();
  const init=root=>{
    if(instances.has(root))return;
    const controller=new AbortController();
    const on=(el,type,fn,options={})=>el?.addEventListener(type,fn,{...options,signal:controller.signal});
    const viewport=root.querySelector('[data-vision-viewport]');
    const track=root.querySelector('[data-vision-track]');
    const stations=track?[...track.querySelectorAll('[data-vision-station]')]:[];
    const dots=[...root.querySelectorAll('[data-vision-dot]')];
    const prev=root.querySelector('[data-vision-prev]');
    const next=root.querySelector('[data-vision-next]');
    const announcer=root.querySelector('[data-vision-announcer]');
    const motion=matchMedia('(prefers-reduced-motion: reduce)');
    let index=0,drag=null,frame=0,point=null,resizeObserver=null,chapterObserver=null;
    const timers=new Set();
    const width=()=>viewport?.clientWidth||1;
    const show=(instant=false,announce=true)=>{
      if(!track||!stations.length)return;
      index=Math.max(0,Math.min(index,stations.length-1));
      root.dataset.activeStation=String(index+1);
      track.style.transitionDuration=instant||motion.matches?'0s':'';
      track.style.transform=`translate3d(${-index*width()}px,0,0)`;
      stations.forEach((station,i)=>{
        station.inert=i!==index;
        station.setAttribute('aria-hidden',String(i!==index));
      });
      dots.forEach((dot,i)=>{
        dot.classList.toggle('is-active',i===index);
        dot.setAttribute('aria-current',String(i===index));
      });
      if(prev)prev.disabled=index===0;
      if(next)next.disabled=index===stations.length-1;
      if(announce&&announcer)announcer.textContent=stations[index].getAttribute('aria-label')||'';
    };
    const move=i=>{index=i;show()};
    on(prev,'click',()=>move(index-1));
    on(next,'click',()=>move(index+1));
    dots.forEach(dot=>on(dot,'click',()=>move(Number(dot.dataset.index)||0)));
    on(viewport,'keydown',event=>{
      if(event.target.closest('input,select,textarea'))return;
      const keys={ArrowLeft:index+1,ArrowRight:index-1,Home:0,End:stations.length-1};
      if(Object.hasOwn(keys,event.key)){event.preventDefault();move(keys[event.key])}
    });
    on(viewport,'dragstart',event=>event.preventDefault());
    on(viewport,'pointerdown',event=>{
      if(!event.isPrimary||event.button!==0||event.target.closest('a,button,input,select,textarea,summary'))return;
      drag={id:event.pointerId,start:event.clientX,current:event.clientX};
      viewport.setPointerCapture(event.pointerId);
      viewport.classList.add('is-dragging');
      if(track)track.style.transitionDuration='0s';
    });
    const renderPoint=()=>{
      frame=0;
      if(!point||motion.matches)return;
      const rect=viewport.getBoundingClientRect();
      const x=Math.max(-1,Math.min(1,((point.x-rect.left)/rect.width-.5)*2));
      const y=Math.max(-1,Math.min(1,((point.y-rect.top)/rect.height-.5)*2));
      Object.entries({'--sun-x':`${x*-1.2}rem`,'--sun-y':`${y*-.7}rem`,'--mount-x':`${x*-2}rem`,'--mount-y':`${y*-.8}rem`,'--field-x':`${x*-3}rem`,'--field-y':`${y*.7}rem`,'--jungle-left':`${x*4}rem`,'--jungle-right':`${x*-4}rem`,'--jungle-y':`${y}rem`,'--tilt-x':`${y*-1.5}deg`,'--tilt-y':`${x*2.4}deg`}).forEach(([key,value])=>viewport.style.setProperty(key,value));
    };
    on(viewport,'pointermove',event=>{
      if(event.pointerType==='mouse'&&!motion.matches){point={x:event.clientX,y:event.clientY};if(!frame)frame=requestAnimationFrame(renderPoint)}
      if(!drag||drag.id!==event.pointerId||!track)return;
      drag.current=event.clientX;
      let delta=drag.current-drag.start;
      if(index===0&&delta>0||index===stations.length-1&&delta<0)delta*=.25;
      track.style.transform=`translate3d(${-index*width()+delta}px,0,0)`;
    });
    const end=event=>{
      if(!drag||drag.id!==event.pointerId)return;
      const delta=drag.current-drag.start;
      if(event.type==='pointerup'&&Math.abs(delta)>Math.min(70,width()*.15))index+=delta<0?1:-1;
      drag=null;
      viewport.classList.remove('is-dragging');
      show();
      if(viewport.hasPointerCapture(event.pointerId))viewport.releasePointerCapture(event.pointerId);
    };
    on(viewport,'pointerup',end);
    on(viewport,'pointercancel',end);
    on(viewport,'lostpointercapture',end);
    if(viewport&&'ResizeObserver'in window){resizeObserver=new ResizeObserver(()=>show(true,false));resizeObserver.observe(viewport)}
    else on(window,'resize',()=>show(true,false),{passive:true});
    const chapters=[...root.querySelectorAll('[data-vision-chapter]')];
    if('IntersectionObserver'in window){
      chapterObserver=new IntersectionObserver(entries=>entries.forEach(entry=>{if(entry.isIntersecting)entry.target.classList.add('is-active')}),{threshold:.12});
      chapters.forEach(chapter=>chapterObserver.observe(chapter));
    }else chapters.forEach(chapter=>chapter.classList.add('is-active'));
    const route=(key,path)=>{
      const value=window.routes?.[key]||`${window.Shopify?.routes?.root||'/'}${path}`;
      return value.endsWith('.js')?value:`${value}.js`;
    };
    const refreshCart=async()=>{
      try{
        const response=await fetch(route('cart_url','cart'),{headers:{Accept:'application/json'},signal:controller.signal});
        if(!response.ok)return;
        const cart=await response.json();
        document.querySelectorAll('[data-vision-cart-count],.travel-cart-count').forEach(el=>el.textContent=String(cart.item_count||0));
      }catch{}
    };
    on(root,'click',async event=>{
      const anchor=event.target.closest('a[href^="#Vision"]');
      if(anchor){
        const target=document.getElementById(anchor.hash.slice(1));
        if(target&&root.contains(target)){event.preventDefault();target.scrollIntoView({behavior:motion.matches?'instant':'smooth',block:'start'});target.setAttribute('tabindex','-1');target.focus({preventScroll:true})}
      }
      const button=event.target.closest('[data-vision-add]');
      if(!button||!root.contains(button)||button.disabled)return;
      const label=button.querySelector('[data-vision-button-label]');
      const id=button.dataset.variantId;
      if(!label||!id)return;
      button.disabled=true;
      button.setAttribute('aria-busy','true');
      label.textContent=button.dataset.addingLabel||'';
      try{
        const response=await fetch(route('cart_add_url','cart/add'),{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id,quantity:1}]})});
        if(!response.ok)throw new Error('cart_add_failed');
        label.textContent=button.dataset.addedLabel||'';
        if(announcer)announcer.textContent=label.textContent;
        void refreshCart();
      }catch{
        label.textContent=button.dataset.errorLabel||'';
        if(announcer)announcer.textContent=label.textContent;
      }finally{
        button.removeAttribute('aria-busy');
        const timer=setTimeout(()=>{label.textContent=button.dataset.defaultLabel||'';button.disabled=false;timers.delete(timer)},1800);
        timers.add(timer);
      }
    });
    on(document,'shopify:block:select',event=>{
      const station=event.target.closest('[data-vision-station]');
      if(station&&root.contains(station))move(stations.indexOf(station));
    });
    root.dataset.ready='true';
    root.dataset.visionVersion='2';
    show(true,false);
    instances.set(root,()=>{controller.abort();resizeObserver?.disconnect();chapterObserver?.disconnect();cancelAnimationFrame(frame);timers.forEach(clearTimeout);instances.delete(root)});
  };
  const all=scope=>{if(scope.matches?.('[data-vision-root]'))init(scope);scope.querySelectorAll('[data-vision-root]').forEach(init)};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>all(document),{once:true});else all(document);
  document.addEventListener('shopify:section:load',event=>all(event.target));
  document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('[data-vision-root]').forEach(root=>instances.get(root)?.()));
})();

